import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import CustomButton from '../customButton';
import NormalButton from '../normalButton/normalButton';
import { ArrowBackIcon, closeCommentBoxIcon, openCommentBoxIcon } from './assets';
import './detailTitleComponent.scss';
import type { DetailTitleComponentProps } from './types';
import { useDetailTitleComponent } from './useDetailTitleComponent';

const EMPTY_TITLE_BLOCK_ARRAY: DetailTitleComponentProps['titleBlockArray'] = [];
/**
 * Default no-op handler standing in for an absent notes toggle callback on the detail title. Accepts no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Header of a detail page: back button, title, an optional row of title and content blocks, a right-hand slot and an optional notes toggle. Renders a skeleton variant until isLoadedData, skeletoning the title itself on the edit-report page, and in the loaded state draws each block as plain content or a text button plus its label and a Biodirect tag when flagged.
 *
 * @param props - Title, title blocks, load state, right component and the notes toggle.
 * @returns The detail page header markup.
 */
const DetailTitleComponent = (props: DetailTitleComponentProps) => {
  const {
    isLoadedData = true,
    isEditReportPage = false,
    titleBlockArray = EMPTY_TITLE_BLOCK_ARRAY,
    rightComponent = undefined,
    hasCommentBox = false,
    commentBoxVisible = false,
    onToggle = NOOP,
    title,
  } = props;
  const { onClickBack } = useDetailTitleComponent(props);

  if (!isLoadedData) {
    return (
      <div className="title-header-container-skeleton">
        <div className="left-container">
          <CustomButton
            id="back-btn"
            style="neutral"
            type="secondary"
            size="small"
            className="back-icon-btn"
            onClick={onClickBack}
            icon={<ReactSVG src={ArrowBackIcon} />}
            ariaLabel={t`Back`}
          />

          {isEditReportPage ? (
            <div className="skeleton-container --title">
              <Skeleton height="100%" />
            </div>
          ) : (
            <span className="title-header">{title}</span>
          )}

          <div className="skeleton-container">
            <Skeleton height="100%" style={{ width: 'calc(100% - 300px)' }} />
          </div>
        </div>

        <div className="right-container" />

        {hasCommentBox && (
          <div className="right-container">
            {commentBoxVisible ? (
              <div className="comment-box-opened">
                <div className="header-title">Notes</div>
                <button
                  type="button"
                  className="close-button"
                  onClick={onToggle}
                  aria-label="Close comment box"
                >
                  <img src={closeCommentBoxIcon} alt="Close" />
                </button>
              </div>
            ) : (
              <div className="comment-box-closed">
                <button type="button" className="close-button" onClick={onToggle}>
                  <span className="notes-label"> Notes</span>{' '}
                  <img src={openCommentBoxIcon} alt="" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="title-header-container">
      <div className="main-container">
        <div className="left-container">
          <CustomButton
            id="back-btn"
            style="neutral"
            type="secondary"
            size="small"
            className="back-icon-btn"
            icon={<ReactSVG src={ArrowBackIcon} />}
            onClick={onClickBack}
            ariaLabel={t`Back`}
          />

          <span className="title-header">{title}</span>

          {!!titleBlockArray?.length && (
            <div className="title-block-container">
              {_.map(titleBlockArray, (element, index) => (
                <div key={index} className="title-block">
                  <div className="title-block__title">{element.title}</div>

                  <div className="item-content">
                    {element.isTextButton ? (
                      <NormalButton
                        id={`${element.content}-${index}`}
                        isText
                        buttonName={element.content}
                        onClick={element.handleClick}
                      />
                    ) : (
                      <div className="title-block__content">{element.content}</div>
                    )}

                    {!!element.label && <div className="title-block__label">{element.label}</div>}

                    {element.isBiofluxDirect && (
                      <div className="title-block__label --biodirect">{t`Biodirect`}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {!!rightComponent && <div className="right-header">{rightComponent}</div>}

        {hasCommentBox && (
          <div className="right-container">
            {commentBoxVisible ? (
              <div className="comment-box-opened">
                <div className="header-title">Notes</div>
                <button
                  type="button"
                  className="close-button"
                  onClick={onToggle}
                  aria-label="Close comment box"
                >
                  <img src={closeCommentBoxIcon} alt="Close" />
                </button>
              </div>
            ) : (
              <div className="comment-box-closed">
                <button type="button" className="close-button" onClick={onToggle}>
                  <span className="notes-label"> Notes</span>{' '}
                  <img src={openCommentBoxIcon} alt="" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailTitleComponent;
